/**
 * Wrapper for country selector: Slim Select (loaded from layout) + countries-list.
 * Constructor receives selector; init() runs on that element. DPR theme via .auth-page .ss-* in auth.scss.
 */
import { getCountryDataList } from 'countries-list';

declare global {
  interface Window {
    SlimSelect?: new (config: SlimSelectConfig) => SlimSelectInstance;
  }
}

interface SlimSelectConfig {
  select: string | HTMLSelectElement;
  data: SlimOption[];
  settings?: Record<string, unknown>;
  events?: Record<string, () => void>;
}

interface SlimSelectInstance {
  destroy?: () => void;
}

export interface CountrySelectOptions {
  placeholder?: string;
  /** Initial country code (e.g. "KR") to set as selected after init */
  initialValue?: string;
}

interface SlimOption {
  value: string;
  text: string;
  id?: string;
  placeholder?: boolean;
  selected?: boolean;
}

function buildCountryOptions(placeholderText: string, initialValue?: string): SlimOption[] {
  const list = getCountryDataList();
  const initial = initialValue ? String(initialValue).toUpperCase().trim() : "";
  const options = list
    .map((c) => ({
      value: c.iso2,
      text: c.name,
      id: c.iso2,
      selected: initial && c.iso2 === initial,
    }))
    .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: "base" }));
  return [{ value: "", text: placeholderText, id: "", placeholder: true }, ...options];
}

export class CountrySelect {
  private readonly selector: string;
  private readonly options: CountrySelectOptions;
  private instance: SlimSelectInstance | null = null;

  constructor(selector: string, options: CountrySelectOptions = {}) {
    this.selector = selector;
    this.options = options;
  }

  /**
   * Bind Slim Select to the element matching this.selector. No-op if not found or SlimSelect not loaded.
   */
  init(): SlimSelectInstance | null {
    const SlimSelect = typeof window !== 'undefined' ? window.SlimSelect : undefined;
    if (!SlimSelect) return null;

    const el = document.querySelector<HTMLSelectElement>(this.selector);
    if (!el) return null;

    const placeholder =
      el.dataset.placeholder ?? this.options.placeholder ?? "Select country";
    const initialValue = el.dataset.initialValue ?? this.options.initialValue ?? "";
    const data = buildCountryOptions(placeholder, initialValue || undefined);

    this.instance = new SlimSelect({
      select: el,
      data,
      settings: {
        placeholderText: placeholder,
        allowDeselect: true,
        openPosition: 'auto',
        minSelected: 0,
        showSearch: true,
        searchPlaceholder: placeholder,
        searchHighlight: true,
      },
      events: {
        afterChange: () => {
          el.dispatchEvent(new Event('change', { bubbles: true }));
        },
      },
    });

    // Let Lenis ignore scroll inside the dropdown so the list can scroll
    document.querySelectorAll('.ss-content .ss-list').forEach((list) => {
      list.setAttribute('data-lenis-prevent', '');
    });

    return this.instance;
  }

  getInstance(): SlimSelectInstance | null {
    return this.instance;
  }

  destroy(): void {
    if (this.instance && typeof this.instance.destroy === 'function') {
      this.instance.destroy();
      this.instance = null;
    }
  }
}
